import Image from "next/image"
import Link from "next/link"
import { ArrowRight, ChevronDown } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  CommandPaletteTriggerIsland,
  LocaleCurrencySwitcherIsland,
  MobileMenuIsland,
  NavigationScrollStateIsland,
  NavSearchIsland,
  ThemeToggleIsland,
  UtilityIconsIsland,
} from "../navigation-islands"
import type { NavigationCta, NavigationItem, NavigationLogo, NavigationShellProps } from "./navigation-types"

const fallbackItems: NavigationItem[] = [
  { label: "Features", href: "#features" },
  { label: "Proof", href: "#proof" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
]

function normalizeLogo(brand?: string, logo?: NavigationLogo): NavigationLogo {
  return logo ?? { label: brand ?? "Hiveku Site", href: "/" }
}

function LogoMark({ logo }: { logo: NavigationLogo }) {
  const href = logo.href ?? "/"
  return (
    <Link href={href} className="group inline-flex min-w-0 items-center gap-3 font-heading text-base font-semibold tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4">
      {logo.image ? (
        <Image src={logo.image.src} alt={logo.image.alt} width={logo.image.width ?? 36} height={logo.image.height ?? 36} className="size-9 rounded-full object-cover shadow-[var(--shadow-elegant)]" />
      ) : (
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground shadow-[var(--shadow-elegant)] transition-transform duration-300 group-hover:scale-105">
          {logo.label.slice(0, 1).toUpperCase()}
        </span>
      )}
      <span className="min-w-0">
        <span className="block max-w-[12rem] truncate sm:max-w-none">{logo.label}</span>
        {logo.tagline && <span className="block max-w-[12rem] truncate text-xs font-normal text-muted-foreground">{logo.tagline}</span>}
      </span>
    </Link>
  )
}

function DropdownPanel({ item }: { item: NavigationItem }) {
  const columns = item.dropdownColumns ?? (item.dropdownItems ? [{ items: item.dropdownItems }] : [])
  if (columns.length === 0 && !item.featuredCard) return null

  return (
    <div className="invisible absolute left-1/2 top-full z-50 mt-3 w-[min(44rem,calc(100vw-2rem))] -translate-x-1/2 rounded-[1.75rem] border border-border/70 bg-background p-3 text-foreground opacity-0 shadow-elegant transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
      <div className={cn("grid gap-3", item.featuredCard ? "lg:grid-cols-[1fr_16rem]" : "")}> 
        <div className="grid gap-2 sm:grid-cols-2">
          {columns.map((column, columnIndex) => (
            <div key={`${item.label}-${column.title ?? columnIndex}`} className="rounded-card bg-muted/45 p-2">
              {column.title && <p className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{column.title}</p>}
              {column.items.map((dropdownItem) => (
                <Link key={dropdownItem.href} href={dropdownItem.href} className="block rounded-xl px-3 py-2.5 transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    {dropdownItem.label}
                    {dropdownItem.badge && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[0.65rem] uppercase tracking-[0.12em] text-primary">{dropdownItem.badge}</span>}
                  </span>
                  {dropdownItem.description && <span className="mt-1 block text-xs leading-5 text-muted-foreground">{dropdownItem.description}</span>}
                </Link>
              ))}
            </div>
          ))}
        </div>
        {item.featuredCard && (
          <Link href={item.featuredCard.href} className="group/card overflow-hidden rounded-card bg-foreground p-4 text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {item.featuredCard.eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.16em] text-background/60">{item.featuredCard.eyebrow}</p>}
            <p className="mt-3 font-heading text-xl font-semibold tracking-tight">{item.featuredCard.title}</p>
            {item.featuredCard.description && <p className="mt-2 text-sm leading-6 text-background/68">{item.featuredCard.description}</p>}
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-accent">
              Explore
              <ArrowRight className="size-4 transition-transform group-hover/card:translate-x-1" />
            </span>
          </Link>
        )}
      </div>
    </div>
  )
}

function DesktopLinks({ items, centered = false }: { items: NavigationItem[]; centered?: boolean }) {
  return (
    <nav aria-label="Primary" className={cn("hidden items-center gap-1 md:flex", centered && "justify-center")}>
      {items.map((item) => {
        const hasDropdown = Boolean(item.dropdownItems?.length || item.dropdownColumns?.length || item.featuredCard)
        return (
          <div key={item.href} className="group relative">
            <Link
              href={item.href}
              aria-current={item.current ? "page" : undefined}
              aria-haspopup={hasDropdown ? "menu" : undefined}
              className={cn(
                "inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                item.current && "bg-muted text-foreground",
              )}
            >
              <span>{item.label}</span>
              {item.badge && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-primary">{item.badge}</span>}
              {hasDropdown && <ChevronDown className="size-3.5 transition-transform group-hover:rotate-180" />}
            </Link>
            <DropdownPanel item={item} />
          </div>
        )
      })}
    </nav>
  )
}

function CtaGroup({ ctas = [] }: { ctas?: NavigationCta[] }) {
  if (ctas.length === 0) return null
  return (
    <div className="hidden items-center gap-2 sm:flex">
      {ctas.slice(0, 2).map((cta) => (
        <Button key={cta.href} asChild size="sm" variant={cta.variant === "secondary" ? "outline" : cta.variant === "ghost" ? "ghost" : "default"} className="group shrink-0 rounded-full px-4">
          <Link href={cta.href}>
            {cta.label}
            {cta.variant !== "ghost" && <ArrowRight className="transition-transform duration-300 group-hover:translate-x-0.5" />}
          </Link>
        </Button>
      ))}
    </div>
  )
}

function AnnouncementBar({ announcement }: Pick<NavigationShellProps, "announcement">) {
  if (!announcement) return null
  return (
    <div className="border-b border-border/60 bg-foreground px-4 py-2 text-background">
      <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center text-xs font-medium sm:text-sm">
        <span>{announcement.text}</span>
        {announcement.promoCode && <span className="rounded-full bg-background/12 px-2 py-0.5 font-mono text-xs">{announcement.promoCode}</span>}
        {announcement.countdownLabel && <span className="text-background/68">{announcement.countdownLabel}</span>}
        {announcement.href && <Link href={announcement.href} className="font-semibold text-accent underline-offset-4 hover:underline">{announcement.linkLabel ?? "Learn more"}</Link>}
      </div>
    </div>
  )
}

export function NavigationShell({
  navId,
  brand,
  logo,
  items = fallbackItems,
  ctas = [{ label: "Start project", href: "#contact" }],
  announcement,
  layout = "default",
  behavior = "solid-sticky",
  showSearch = false,
  showCommand = false,
  showThemeToggle = false,
  showLocaleSwitcher = false,
  showCart = false,
  showAccount = false,
  cartCount = 0,
  auth,
  socials = [],
  className,
}: NavigationShellProps) {
  const normalizedLogo = normalizeLogo(brand, logo)
  const resolvedNavId = (navId ?? `nav-${layout}-${behavior}`).replace(/[^a-z0-9-]/gi, "-")
  const primaryCta = ctas[0]

  if (layout === "sidebar") {
    return (
      <aside className={cn("sticky top-0 hidden h-dvh w-72 shrink-0 border-r border-border/70 bg-background p-5 text-foreground lg:flex lg:flex-col", className)}>
        <LogoMark logo={normalizedLogo} />
        <div className="mt-8 grid gap-2">
          {items.map((item) => <Link key={item.href} href={item.href} className="rounded-card px-4 py-3 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">{item.label}</Link>)}
        </div>
        <div className="mt-auto"><CtaGroup ctas={ctas} /></div>
      </aside>
    )
  }

  if (layout === "bottom") {
    return (
      <nav aria-label="Primary" className={cn("fixed inset-x-3 bottom-3 z-50 rounded-[1.75rem] border border-border/70 bg-background/92 p-2 shadow-elegant backdrop-blur-xl md:hidden", className)}>
        <div className="grid grid-cols-4 gap-1">
          {items.slice(0, 4).map((item) => <Link key={item.href} href={item.href} className="rounded-card px-2 py-2 text-center text-xs font-semibold text-muted-foreground hover:bg-muted hover:text-foreground">{item.label}</Link>)}
        </div>
      </nav>
    )
  }

  const stickyClass = behavior === "static" ? "relative" : "sticky top-0"
  const surfaceClass = behavior === "transparent"
    ? "border-background/10 bg-background/8 text-foreground backdrop-blur-xl data-[scrolled=true]:border-border/70 data-[scrolled=true]:bg-background/92"
    : "border-border/60 bg-background/90 text-foreground backdrop-blur-xl"
  const floatingClass = layout === "floating-pill" ? "mx-auto mt-4 max-w-7xl rounded-full border shadow-[var(--shadow-card)]" : "border-b"
  const shrinkClass = behavior === "shrink-on-scroll" ? "data-[scrolled=true]:[&_div[data-nav-inner]]:min-h-12 data-[scrolled=true]:shadow-sm" : ""
  const hideClass = behavior === "hide-on-scroll" ? "data-[scroll-dir=down]:-translate-y-full" : ""

  const leftItems = layout === "centered-logo" ? items.slice(0, Math.ceil(items.length / 2)) : items
  const rightItems = layout === "centered-logo" ? items.slice(Math.ceil(items.length / 2)) : []

  return (
    <header id={resolvedNavId} data-scrolled="false" data-scroll-dir="up" className={cn("z-50 transition-all duration-300", stickyClass, surfaceClass, floatingClass, shrinkClass, hideClass, className)}>
      {(behavior === "transparent" || behavior === "hide-on-scroll" || behavior === "shrink-on-scroll") && <NavigationScrollStateIsland targetId={resolvedNavId} />}
      <AnnouncementBar announcement={announcement} />
      <div data-nav-inner className={cn("mx-auto flex min-h-16 w-full max-w-7xl items-center gap-4 px-4 transition-all duration-300 sm:px-6 lg:px-8", layout === "right-aligned" ? "justify-end" : "justify-between")}>
        {layout === "right-aligned" && <div className="mr-auto hidden text-sm text-muted-foreground md:block">Navigation</div>}
        {layout !== "centered-logo" && <LogoMark logo={normalizedLogo} />}
        {layout === "centered-logo" ? (
          <div className="grid flex-1 grid-cols-[1fr_auto_1fr] items-center gap-4">
            <DesktopLinks items={leftItems} />
            <LogoMark logo={normalizedLogo} />
            <div className="justify-self-end"><DesktopLinks items={rightItems} /></div>
          </div>
        ) : (
          <div className={cn("hidden flex-1 md:flex", layout === "center-links" || layout === "floating-pill" ? "justify-center" : layout === "right-aligned" ? "justify-end" : "justify-center")}> 
            <DesktopLinks items={items} centered />
          </div>
        )}
        <div className="flex items-center gap-2">
          {showSearch && <NavSearchIsland />}
          {showCommand && <CommandPaletteTriggerIsland items={items} />}
          {showThemeToggle && <ThemeToggleIsland />}
          {showLocaleSwitcher && <LocaleCurrencySwitcherIsland />}
          {(showCart || showAccount) && <UtilityIconsIsland showCart={showCart} showAccount={showAccount} cartCount={cartCount} auth={auth} />}
          <CtaGroup ctas={ctas} />
          <MobileMenuIsland brand={normalizedLogo.label} items={items} ctas={ctas} cta={primaryCta} socials={socials} showSearch={showSearch || showCommand} />
        </div>
      </div>
    </header>
  )
}
